import { useEffect, useState } from 'react';
import type { EnvironmentalCriteriaConfig, UpdateCriteriaConfigPayload } from './types';

interface CriteriaConfigPanelProps {
  config: EnvironmentalCriteriaConfig | null;
  canEdit: boolean;
  onSave: (payload: UpdateCriteriaConfigPayload) => Promise<void>;
}

export function CriteriaConfigPanel({ config, canEdit, onSave }: CriteriaConfigPanelProps) {
  const [form, setForm] = useState<UpdateCriteriaConfigPayload>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config) return;
    setForm({
      significanceThreshold: config.significanceThreshold,
      weightSeverity: config.weightSeverity,
      weightFrequency: config.weightFrequency,
      weightLegalRelevance: config.weightLegalRelevance,
      weightStakeholderConcern: config.weightStakeholderConcern,
      scaleMax: config.scaleMax,
    });
  }, [config]);

  if (!config) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await onSave(form);
      setMessage('Criterios actualizados. Se recalculó la significancia de todos los aspectos.');
    } catch {
      setError('No se pudieron guardar los criterios.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-card border border-border rounded-lg p-4 space-y-3">
      <div>
        <h2 className="font-semibold text-sm">Criterios de significancia</h2>
        <p className="text-muted-foreground text-xs">
          Puntaje = severidad×P + frecuencia×F + relevancia legal×L + partes interesadas×I. Umbral
          actual: {config.significanceThreshold} (escala 1–{config.scaleMax}).
        </p>
      </div>
      <form className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" onSubmit={handleSubmit}>
        <label className="text-xs space-y-1">
          <span>Umbral</span>
          <input
            type="number"
            min={1}
            max={200}
            className="input w-full"
            disabled={!canEdit}
            value={form.significanceThreshold ?? ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                significanceThreshold: Number.parseInt(e.target.value, 10),
              }))
            }
          />
        </label>
        <label className="text-xs space-y-1">
          <span>Peso severidad</span>
          <input
            type="number"
            min={1}
            max={10}
            className="input w-full"
            disabled={!canEdit}
            value={form.weightSeverity ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, weightSeverity: Number.parseInt(e.target.value, 10) }))
            }
          />
        </label>
        <label className="text-xs space-y-1">
          <span>Peso frecuencia</span>
          <input
            type="number"
            min={1}
            max={10}
            className="input w-full"
            disabled={!canEdit}
            value={form.weightFrequency ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, weightFrequency: Number.parseInt(e.target.value, 10) }))
            }
          />
        </label>
        <label className="text-xs space-y-1">
          <span>Peso legal</span>
          <input
            type="number"
            min={1}
            max={10}
            className="input w-full"
            disabled={!canEdit}
            value={form.weightLegalRelevance ?? ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                weightLegalRelevance: Number.parseInt(e.target.value, 10),
              }))
            }
          />
        </label>
        <label className="text-xs space-y-1">
          <span>Peso partes interesadas</span>
          <input
            type="number"
            min={1}
            max={10}
            className="input w-full"
            disabled={!canEdit}
            value={form.weightStakeholderConcern ?? ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                weightStakeholderConcern: Number.parseInt(e.target.value, 10),
              }))
            }
          />
        </label>
        <label className="text-xs space-y-1">
          <span>Escala máx.</span>
          <input
            type="number"
            min={3}
            max={10}
            className="input w-full"
            disabled={!canEdit}
            value={form.scaleMax ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, scaleMax: Number.parseInt(e.target.value, 10) }))
            }
          />
        </label>
        {canEdit && (
          <div className="col-span-full">
            <button type="submit" className="btn btn-secondary btn-sm" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar criterios y recalcular'}
            </button>
          </div>
        )}
      </form>
      {message && <p className="text-xs text-emerald-600">{message}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </section>
  );
}
